import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import type { ResetPasswordOutput, SignInOutput } from 'aws-amplify/auth';

import { setRememberMe } from '@/lib/amplify';

import type { CurrentUser, RolType } from '../types';

export interface SignInResult {
  isSignedIn: boolean;
  nextStep: SignInOutput['nextStep'];
}

export const authService = {
  async signIn(username: string, password: string, rememberMe: boolean): Promise<SignInResult> {
    setRememberMe(rememberMe);
    const result = await signIn({ username, password });
    return { isSignedIn: result.isSignedIn, nextStep: result.nextStep };
  },

  async confirmSignIn(challengeResponse: string): Promise<SignInResult> {
    const result = await confirmSignIn({ challengeResponse });
    return { isSignedIn: result.isSignedIn, nextStep: result.nextStep };
  },

  async signOut(): Promise<void> {
    await signOut();
  },

  async forgotPassword(username: string): Promise<ResetPasswordOutput> {
    return await resetPassword({ username });
  },

  async confirmForgotPassword(
    username: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword, newPassword });
  },

  async getCurrentUserFromSession(): Promise<CurrentUser | null> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const idTokenPayload = session.tokens?.idToken?.payload;
      if (!idTokenPayload) return null;

      const role = idTokenPayload['custom:role'];
      if (typeof role !== 'string') {
        throw new Error('User has no custom:role attribute');
      }
      const validRoles: RolType[] = [
        'administrador',
        'tecnico_laboratorio',
        'profesional',
        'establecimiento',
      ];
      if (!validRoles.includes(role as RolType)) {
        throw new Error(`Invalid custom:role value: ${role}`);
      }

      const email = typeof idTokenPayload.email === 'string' ? idTokenPayload.email : '';
      const nombre = typeof idTokenPayload.name === 'string' ? idTokenPayload.name : undefined;

      return {
        username: user.username,
        email,
        nombre,
        rol: role as RolType,
      };
    } catch {
      return null;
    }
  },

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      return token ?? null;
    } catch {
      return null;
    }
  },
};
